// ============= Imports =============
import { API_CONFIG } from '../../config';
import {
  FetchDataProps,
  ApiResponse,
  SharePointUserProfile,
} from '../components/container/forms/types';
import { tokenManager } from '../utils/tokenManager';

// ============= Constants =============
const SHAREPOINT_SCOPE =
  'api://24f835fd-ad36-4d11-a440-3cb3a93a6bcb/access_as_user';
const HEADERS = {
  Accept: 'application/json;odata=verbose',
  'Content-Type': 'application/json',
};

// ============= Types =============
interface SharePointHeaders extends Record<string, string> {
  Authorization: string;
  Accept: string;
  'Content-Type': string;
}

// ============= Utility Functions =============
/**
 * Constructs the API URL based on environment configuration
 * @param endpoint - The API endpoint
 * @returns Complete API URL
 */
const getApiUrl = (endpoint: string): string => {
  const config = API_CONFIG[import.meta.env.MODE as keyof typeof API_CONFIG];
  return `${config.baseUrl}/${endpoint}`;
};

/**
 * Creates headers for SharePoint API request
 * @param token - Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string): SharePointHeaders => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

/**
 * Constructs the endpoint URL with encoded username
 * @param username - Username to search for
 * @returns Formatted endpoint string
 */
const constructEndpoint = (username: string): string => {
  return `Sharepoint/GetPropertiesFor?accountName=${username}`;
};

// ============= Main Function =============
/**
 * Fetches user profile properties from SharePoint
 * @param getAccessToken - Function to acquire access token
 * @param searchValue - Username to search for
 * @returns Promise with API response containing user profile data or error
 */
export const fetchSharePointUser = async ({
  getAccessToken,
  searchValue,
}: FetchDataProps): Promise<ApiResponse<SharePointUserProfile>> => {
  try {
    console.log('🔄 Fetching SharePoint data...');

    // Token acquisition with required scopes
    const token = await getAccessToken([SHAREPOINT_SCOPE], {
      skipStorageToken: true,
    });
    if (!token) {
      throw new Error('Failed to acquire SharePoint token');
    }
    tokenManager.logTokenInfo(token, 'SharePoint');

    // Endpoint construction
    const endpoint = constructEndpoint(searchValue);
    const apiUrl = getApiUrl(endpoint);
    console.log('🌐 Making request to:', apiUrl);

    // API request
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as SharePointUserProfile;
    console.log('✅ SharePoint data fetched successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error fetching SharePoint data:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
